import classNames from 'classnames'
import { FC, memo, useCallback, useMemo } from 'react'

import { EntityType, ProjectType } from '@crew/enums/domain'
import { getDefaultTabValue } from '@crew/utils/enum'
import {
  DirectMessageTabs,
  EventDetailTabs,
  FileDetailListTabs,
  ProjectDetailTabs,
  TaskDetailListTabs,
} from 'enums/app'

import { CrewLink, LinkColor } from '../crewLink/crewLink'
import { CrewEntityIcon } from '../crewEntityIcon/crewEntityIcon'
import { useAppDispatch, useAppSelector } from 'states/hooks'
import { useChatThreadListService, useChatTimelineService } from '@crew/states'
import { ChatView } from '@crew/enums/app'

// entityType = ChatMessage の場合の付加情報
type ChatMessageInfo = {
  chatRoomId: string
  chatRoomEntityType: EntityType
  chatRoomEntityRecordId: string
  projectType: ProjectType
}

type CrewEntityLinkProps = {
  /** エンティティID */
  id: string
  /** 表示名 */
  displayName: string
  /** エンティティタイプ */
  entityType: EntityType
  /** エンティティアイコンを表示するかどうか */
  showEntityIcon?: boolean
  /** CSSクラス名 */
  className?: string
  /** リンクの色 */
  color?: LinkColor
  /** クリック時のコールバック */
  onClick?: () => void
  /** タイトル属性 */
  title?: string
  /** クリックイベントの伝播を停止するかどうか */
  stopPropagation?: boolean
  /** チャットメッセージ情報（entityTypeがChatMessageの場合） */
  chatMessageInfo?: ChatMessageInfo
}

/**
 * エンティティへのリンクコンポーネント
 *
 * @param id - エンティティのID（ChatMessageの場合はmessageId）
 * @param entityType - エンティティタイプ
 * @param chatMessageInfo - ChatMessageの場合の追加情報
 * @description
 * - CrewEntityLinkは、エンティティタイプに応じてURLを生成しリンクを表示するダムコンポーネント。URL生成に必要な情報はpropsで受け取る。
 * - それに対して、CrewRelatedItemLinkはコンポーネント内部でAPIを呼び出し、URL生成に必要な情報を取得するスマートコンポーネント。
 * - 一覧表示などでAPIリクエストが大量に飛んで動作遅延が発生する可能性がある場合はCrewEntityLinkを使用し、そうでない場合はCrewRelatedItemLinkを使用する想定。
 * @example
 * // 通常のタスクリンク
 * <CrewEntityLink id="task123" entityType={EntityType.Task} displayName="タスク" />
 *
 * // チャットメッセージリンク
 * <CrewEntityLink id="msg456" entityType={EntityType.ChatMessage}
 *   chatMessageInfo={{ entityType: EntityType.Task, entityRecordId: "task123", projectType: ProjectType.Project }} />
 */
export const CrewEntityLink: FC<CrewEntityLinkProps> = memo((props) => {
  const {
    id,
    displayName,
    entityType,
    showEntityIcon = false,
    className,
    color,
    onClick,
    title,
    chatMessageInfo,
    stopPropagation,
  } = props

  const dispatch = useAppDispatch()
  // Sliceの操作を行うためのServiceを取得
  const chatTimelineService = useChatTimelineService(dispatch)
  const chatThreadListService = useChatThreadListService(dispatch)

  // チャットのトピック表示形式を取得する
  const currentDisplayFormat = useAppSelector(
    (state) => state.message.chat.current.displayFormat
  )

  // チャットメッセージのURLを生成
  const generateChatMessageUrl = (
    info: ChatMessageInfo,
    messageId: string
  ): string => {
    const messageIdParam = `?messageId=${messageId}`

    switch (info.chatRoomEntityType) {
      case EntityType.Project:
        if (info.projectType === ProjectType.DirectChannel) {
          return `/direct-channels/${info.chatRoomEntityRecordId}/${getDefaultTabValue(DirectMessageTabs)}${messageIdParam}`
        } else if (info.projectType === ProjectType.Project) {
          return `/${EntityType.Project}/${info.chatRoomEntityRecordId}/chat${messageIdParam}`
        } else if (info.projectType === ProjectType.InstantChannel) {
          return `/group-chats/${info.chatRoomEntityRecordId}/${getDefaultTabValue(DirectMessageTabs)}${messageIdParam}`
        }
        return ''
      case EntityType.Event:
        return `/${EntityType.Event}/${info.chatRoomEntityRecordId}/detail${messageIdParam}`
      case EntityType.Task:
        return `/${EntityType.Task}/${info.chatRoomEntityRecordId}/workHistory${messageIdParam}`
      case EntityType.File:
        return `/${EntityType.File}/${info.chatRoomEntityRecordId}/updatedHistory${messageIdParam}`
      default:
        return '#'
    }
  }

  // チャットメッセージ以外のエンティティのURLを生成
  const generateEntityUrl = (type: EntityType, id: string): string => {
    switch (type) {
      case EntityType.Project:
        return `/${EntityType.Project}/${id}/${getDefaultTabValue(ProjectDetailTabs)}`
      case EntityType.Task:
        return `/${EntityType.Task}/${id}/${getDefaultTabValue(TaskDetailListTabs)}`
      case EntityType.Event:
        return `/${EntityType.Event}/${id}/${getDefaultTabValue(EventDetailTabs)}`
      case EntityType.File:
        return `/${EntityType.File}/${id}/${getDefaultTabValue(FileDetailListTabs)}`
      default:
        return '#'
    }
  }

  // TODO: https://break-tmc.atlassian.net/browse/CREW-17853 の対応。
  //       URLにmessageIdが含まれる場合、そのメッセージにスクロール位置を合わせるとき、scrollToMessageIdやselectedMessageIdと処理が競合してしまい
  //       スクロール位置を合わせられなくなることから暫定対応として、リンククリック時に競合する値をリセットしてしまうことにする。
  //       下記タスクで恒久対応を行う際は、この処理は削除する。
  //       https://break-tmc.atlassian.net/browse/CREW-17941
  const handleLinkClick = useCallback(
    (event?: React.MouseEvent) => {
      // イベントの伝播を停止して、親要素のクリックイベントが発火しないようにする
      if (event && stopPropagation) {
        event.stopPropagation()
      }

      // id（=chatMessageId）やその付加情報が存在する場合のみスクロール位置をリセット
      if (id && chatMessageInfo) {
        if (currentDisplayFormat === ChatView.Timeline) {
          // タイムライン形式の場合
          // スクロール位置をリセット
          chatTimelineService.resetTimelineScrollToMessageId({
            chatRoomId: chatMessageInfo?.chatRoomId || '',
          })
          chatTimelineService.resetTimelineSelectedMessageId({
            chatRoomId: chatMessageInfo?.chatRoomId || '',
          })
        } else if (currentDisplayFormat === ChatView.ThreadList) {
          // スレッドリスト形式の場合
          // スクロール位置をリセット
          chatThreadListService.resetThreadListScrollToMessageId({
            chatRoomId: chatMessageInfo?.chatRoomId || '',
          })
          chatThreadListService.resetThreadListSelectedMessageId({
            chatRoomId: chatMessageInfo?.chatRoomId || '',
          })
        }
      }

      if (onClick) {
        onClick()
      }
    },
    [
      chatMessageInfo,
      chatThreadListService,
      chatTimelineService,
      currentDisplayFormat,
      id,
      onClick,
      stopPropagation,
    ]
  )

  // リンク生成
  const href = useMemo(() => {
    // チャットメッセージリンクの場合
    if (
      entityType === EntityType.ChatMessage &&
      chatMessageInfo?.chatRoomEntityType &&
      chatMessageInfo?.chatRoomEntityRecordId
    ) {
      return generateChatMessageUrl(chatMessageInfo, id)
    }

    // 通常のエンティティリンクの場合
    return generateEntityUrl(entityType, id)
  }, [entityType, id, chatMessageInfo])

  return (
    <CrewLink
      color={color}
      to={href}
      title={title || displayName}
      className={classNames('inline-flex items-center', className)}
      onClick={handleLinkClick}
    >
      {showEntityIcon && (
        <CrewEntityIcon entityType={entityType} className="inline-block mr-1" />
      )}
      {displayName}
    </CrewLink>
  )
})
